import { AppConfig } from "@/api/config";

export type ApiConfig = {
  baseUrl: string;
  defaultGroupId?: string;
};

/**
 * API configuration resolved at build time
 */
export const apiConfig: ApiConfig = {
  baseUrl: AppConfig.apiBaseUrl ?? "",
  defaultGroupId: "DEFAULT",
};

/**
 * API route definitions
 */
export const paths = {
  v1: {
    /* ==========================
       Files
       ========================== */
    files: () => `/api/v1/files`,
    getFiles: () => `/api/v1/files/list`,
    fileById: (id: string) => `/api/v1/files/${encodeURIComponent(id)}`,
    fileMeta: (id: string) => `/api/v1/files/${encodeURIComponent(id)}/metadata`,

    /* ==========================
       Disbursements
       ========================== */
    disbursements: () => `/api/v1/disbursements`,
    disbursementById: (id: string) => `/api/v1/disbursements/${encodeURIComponent(id)}`,
    disbursementCancel: (id: string) => `/api/v1/disbursements/${encodeURIComponent(id)}/cancel`,

    /* ==========================
       Microsoft / Realtime
       ========================== */
    msGroupMembers: (groupId: string) =>
      `/api/v1/microsoft/groups/${encodeURIComponent(groupId)}/members`,
    msMeetings: () => `/api/v1/microsoft/meetings`,
    findMeetingTimes: () => `/api/v1/microsoft/calendars/find-meeting-times`,
    scheduleMeeting: () => `/api/v1/microsoft/calendars/schedule`,
    getAvailability: () => `/api/v1/microsoft/calendars/availability`,
    // msMe: () => "/api/v1/microsoft/me",
    // msMePhoto: () => "/api/v1/microsoft/me/photo",

    realtimeNegotiate: (userId: string) =>
      `/api/v1/realtime/negotiate?userId=${encodeURIComponent(userId)}`,
    notifyUser: (userId: string) => `/api/v1/realtime/notify/${encodeURIComponent(userId)}`,

    /* ==========================
       Auth / Tokens
       ========================== */
    authConfig: () => `/api/v1/authconfig`,
    authConfigByUser: () => `/api/v1/authconfig/user-login`,
    authConfigRoleSwitchByUser: (userPrincipalName: string) =>
      `/api/v1/authconfig/user-role-switch/${encodeURIComponent(userPrincipalName)}`,
    fluxToken: () => `/api/token/fluxx`,
    authConfigLogoutByUser: (userPrincipalName: string) =>
      `/api/v1/authconfig/user-logout/${encodeURIComponent(userPrincipalName)}`,

    /* ==========================
       Tours
       ========================== */
    userTourDecision: (userPrincipalName: string) =>
      `/api/v1/tour-guide/user-tour/${encodeURIComponent(userPrincipalName)}`,
    userTourShown: (userPrincipalName: string) =>
      `/api/v1/tour-guide/user-tour-shown/${encodeURIComponent(userPrincipalName)}`,

    /* ==========================
       Users / Organizations
       ========================== */
    fluxUserList: () => `/api/v1/users`,
    accessibilityByUser: () => `/api/v1/accessibility`,
    saveAccessibilitySettings: () => `/api/v1/accessibility/save`,
    fluxOrganizationList: () => `/api/v1/organizations`,
    getFluxxUserId: () => `/api/v1/users/getFluxxUserId`,

    /* ==========================
       DB / Tests
       ========================== */
    getDbUser: () => `/api/db/db-user`,
    insertUser: () => `/api/db/tests`,
    listTests: (params: { top: number }) => `/api/db/tests?top=${params.top}`,

    /* ==========================
       Calendar
       ========================== */
    calendar: {
      events: () => `/api/v1/calendar/events`,
      ooo: () => `/api/v1/calendar/ooo`,
    },

    /* ==========================
       Dashboard / Partners
       ========================== */
    activePrograms: () => `/api/v1/programs`,
    getPartnersTable: () => `/api/v1/partners`,
    getPartnersDirectory: () => `/api/v1/partners-directory`,
    getPrimaryContact: () => `/api/v1/partners-directory/users`,
    partnerIssuesRisks: () => `/api/v1/partner-issues-risks`,
    taskPriorities: () => `/api/v1/dashboard/task-priorities`,
    partnerHighlights: () => `/api/v1/dashboard/partner-highlights`,
    impactTargets: () => `/api/v1/dashboard/impact-targets`,
    resources: () => `/api/v1/dashboard/resources`,
    ideaStatsPeriods: () => `/api/v1/dashboard/idea-stats/periods`,
    ideaStatsQuarterly: (quarter: string) => `/api/v1/dashboard/idea-stats/quarterly/${quarter}`,
    kpiCard: () => `/api/v1/kpi-card`,
    keyGuidelines: () => `/api/v1/key-guidelines`,
    keyGuidelineOrder: () => `/api/v1/key-guidelines/order`,
    oversight: () => `/api/v1/oversight`,

    quickLinks: () => `/api/v1/dashboard/quick-links`,
    addQuickLink: () => `/api/v1/dashboard/quick-links/add`,
    reorderQuickLinks: () => `/api/v1/dashboard/quick-links/reorder`,
    deleteQuickLink: (id: string) =>
      `/api/v1/dashboard/quick-links/${encodeURIComponent(id)}/delete`,

    quickLinksConfig: () => `/api/v1/dashboard/quick-links-config`,
    quickLinksConfigUpdate: () => `/api/v1/dashboard/quick-links-config/update`,

    teamCalendar: () => `/api/v1/team-calendar`,
    submissions: () => `/api/v1/submissions`,
    markSubmissionRead: (id: number) => `/api/v1/submissions/${encodeURIComponent(id)}/mark-read`,
    meetings: () => `/api/v1/meetings`,
    learningInsights: () => `/api/v1/learninginsights`,
    paymentsPipeline: () => `/api/v1/dashboard/payments-pipeline`,
      pipelinePriority: () => `/api/v1/dashboard/pipeline-priority`,
    programImpact: () => `/api/v1/program-impact`,

    /* ==========================
       Notifications
       ========================== */
    notifications: () => `/api/v1/notifications`,
    getIdeasTracker: () => `/api/v1/ideas-tracker`,
    notificationsUnreadCount: () => `/api/v1/notifications/unread-count`,
    markChatGroupRead: (groupId: string) =>
      `/api/v1/realtime/chat/groups/${encodeURIComponent(groupId)}/read`,

    /* ==========================
       Analytics
       ========================== */
    analytics: () => `/api/v1/analytics`,
    analyticsInsights: () => `/api/v1/analytics-insights`,

    /* ==========================
       Power BI
       ========================== */
    powerbiReports: (groupId: string) =>
      `/api/v1/powerbi/workspaces/${encodeURIComponent(groupId)}/reports`,
    powerbiEmbedConfig: (groupId: string, reportId: string) =>
      `/api/v1/powerbi/embed/report/${encodeURIComponent(groupId)}/${encodeURIComponent(reportId)}`,

    /* ==========================
       Admin
       ========================== */
    accessMatrix: () => `/api/v1/access/access-matrix`,
    saveAccessChanges: () => `/api/v1/access/save-access`,
    getUserRoles: (upn: string) => `/api/v1/access/user-roles/${encodeURIComponent(upn)}`,
    saveUserRoles: () => `/api/v1/access/user-roles`,
    getUserFeatures: (email: string) => `/api/v1/access/user-features/${encodeURIComponent(email)}`,
    getUserDetails: () => `/api/v1/access/user-details`,
    roleFeatures: (roleCode: string) =>
      `/api/v1/access/role-features/${encodeURIComponent(roleCode)}`,

    /* ==========================
       Chat (from second object)
       ========================== */
    chatCreateGroup: () => `/api/v1/realtime/chat/groups`,
    chatJoinGroup: (groupId: string) =>
      `/api/v1/realtime/chat/groups/${encodeURIComponent(groupId)}/join`,
    chatSendMessage: (groupId: string) =>
      `/api/v1/realtime/chat/groups/${encodeURIComponent(groupId)}/messages`,
    chatLeaveGroup: (groupId: string) =>
      `/api/v1/realtime/chat/groups/${encodeURIComponent(groupId)}/leave`,
    chatMessageHistory: (groupId: string, userId: string) =>
      `/api/v1/realtime/chat/groups/${encodeURIComponent(groupId)}/messages?userId=${encodeURIComponent(userId)}`,
    chatGetMyGroups: (userId: string) =>
      `/api/v1/realtime/chat/my-groups?userId=${encodeURIComponent(userId)}`,

    /* ==========================
       Additional endpoints
       ========================== */
    partnerSubmissions: () => `/api/v1/partner-submissions`,
    disbursementRequests: () => `/api/v1/disbursement-requests`,
    programMetrics: () => `/api/v1/program-metrics`,

    /* ==========================
     Timezones
     ========================== */
    timezones: (upn: string) => `/api/v1/timezones/${encodeURIComponent(upn)}`,
    saveTimezones: () => `/api/v1/timezones/save`,

    timezoneConfig: () => `/api/v1/timezones/timezone-config`,
    timezoneConfigUpdate: () => `/api/v1/timezones/timezone-config/update`,

    /* ==========================
     Dashboard
     ========================== */
    dashboardConfig: () => `/api/v1/dashboard/dashboard-config`,
    dashboardConfigUpdate: () => `/api/v1/dashboard/dashboard-config/update`,

    /* ==========================
   Language Preference
    ======================== */
    language: (upn: string) => `/api/v1/language/${encodeURIComponent(upn)}`,
    saveLanguage: () => `/api/v1/language/save`,

    /* ===================
   Common Configurations
    ======================= */

    commonConfig: () => `/api/v1/commonConfig/common-config`,
    commonConfigUpdate: () => `/api/v1/commonConfig/common-config/update`,
    getRoles: () => `/api/v1/access/roles`,
    allProgramsVisibility: () => `/api/v1/access/all-programs-visibility`,

    /* ==========================
   Maps
    ========================== */
    maps: () => `/api/v1/dashboard/maps`,

    /* ==========================
   Feature Names / Role Names
    ========================== */
    roleNames: () => "/api/v1/commonConfig/role-names",
    widgetNames: () => "/api/v1/commonConfig/widget-names",

    /* ==========================
    Translations
      ========================== */
    translations: () => "/api/v1/translations",

    /* ==========================
    Feedback Categories
      ========================== */
   feedbackCategories: (selectedRole: string) =>
  `/api/v1/dashboard/feedback/categories?role=${encodeURIComponent(selectedRole)}`,
    feedbackSubmit: () => `/api/v1/dashboard/feedback/submit`,

    feedbackFormTranslations: () =>
  "/api/v1/commonConfig/feedback-form-translations",
  },

  /* =========================================
  Pipeline Priority
  ============================================ */
    // Removed duplicate pipelinePriority entry
    // pipelinePriority: () => "/api/v1/dashboard/pipeline-priority",
};
export function buildUrl(path: string) {
  return apiConfig.baseUrl ? `${apiConfig.baseUrl}${path}` : `http://localhost:3002${path}`;
}
